import React, { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmationPopup from '../../pure-components/ConfirmationPopup';
import API from '../../services/api';
import CookieManager from '../../services/CookieManager';
import { toast } from 'react-toastify';
import { ErrorToast, SuccessToast } from '../../pure-components/CustomToast';
import CorporateUserForm from './corporate-user-form';

const columns = [
  { key: 'companyName', label: 'Company Name', width: '180px' },
  { key: 'userName', label: 'User Name', width: '150px' },
  { key: 'email', label: 'Email', width: '220px' },
  { key: 'taxNumber', label: 'Tax Number', width: '150px' },
  { key: 'renewalDate', label: 'Renewal Date', width: '200px' },
  { key: 'status', label: 'Status', width: '120px' },
];

const UserWorld = () => {
  const [userList, setUserList] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showAddNewAdminForm, setShowAddNewAdminForm] = useState(false);

  const profileData = CookieManager.getCookie('user-data')?.length
    ? JSON.parse(CookieManager.getCookie('user-data'))
    : null;

  const fetchCopAdmin = () => {
    setLoading(true);
    let filter = { userType: "CORPORATE" };
    if (profileData?.userType === "ADMIN")
      filter["admin"] = profileData.email;

    API.getUserList({ filter: filter }).then(res => {
      if (res.success) {
        setUserList(Array.isArray(res.data) ? res.data : []);
        // console.log('User List:', res.data);
      } else {
        toast(({ closeToast }) => <ErrorToast message={res.msg} />);
      }
      setLoading(false);
    }).catch(err => {
      setError(err.message || 'Error fetching data');
      setLoading(false);
    });
  };

  useEffect(() => {
    if (profileData?.userType === 'SUPER_ADMIN' || profileData?.userType === 'ADMIN') {
      fetchCopAdmin();
    }
  }, []);

  const suspendUser = (id, isSuspended) => {
    API.suspendUser(selectedAdmin.id, { isSuspended: !selectedAdmin.isSuspended }).then(res => {
      if (res.success) {
        toast(({ closeToast }) => <SuccessToast message={`User ${selectedAdmin.isSuspended ? 'unsuspended' : 'suspended'} successfully!`} />);
        setShowConfirmPopup(false);
        fetchCopAdmin();
      } else {
        toast(({ closeToast }) => <ErrorToast message={res.msg || 'Failed to update user status'} />);
      }
    }).catch(err => {
      toast(({ closeToast }) => <ErrorToast message={err.message || 'Error updating user status'} />);
    });
  };

  const changeCorpUserStatus = (corpUser) => {
    if (profileData?.isSuspended || (new Date(profileData.renewalDate).getTime() < new Date().getTime())) {
      toast(({ closeToast }) => <ErrorToast message={`Can't change user's status, Admin's account suspended!`} />);
      return;
    }
    setSelectedAdmin({ id: corpUser._id, email: corpUser.email, isSuspended: corpUser.isSuspended });
    setShowConfirmPopup(true);
  };

  // Search filter logic
  const filteredUsers = userList.filter(user => {
    const searchLower = search.toLowerCase();
    return (
      (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
      (user.companyName && user.companyName.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="p-4 max-w-full">
      {/* Search and Add Button */}
      <div className="flex flex-row items-start sm:items-center gap-4 mb-6">
        <div className="relative sm:flex-1 min-w-[200px] max-w-md">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <SearchIcon fontSize="small" className="text-gray-400" />
          </span>
          <input
            type="text"
            placeholder="Search users..."
            className="pl-10 pr-3 h-12 border border-gray-200 rounded-md text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
   <button 
     className="group flex items-center justify-center sm:justify-start gap-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap h-12 w-12 sm:w-auto px-3 sm:px-0 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-blue-200" 
     onClick={() => setShowAddNewAdminForm(true)}
   > 
     <div className="sm:ml-6"> 
       <AddIcon fontSize="small" className="text-white" /> 
     </div> 
     <span className="tracking-wide mr-6 hidden sm:inline">Add Users</span> 
   </button>
      </div>

      {/* Responsive Table Container */}
      <div className="bg-white rounded-lg shadow-sm border(border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            Loading...
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f7f8fa] border-b border-gray-200">
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className="text-gray-700 font-semibold px-4 py-3 text-left whitespace-nowrap"
                      style={{ width: col.width, minWidth: col.width }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-12 text-gray-500">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, idx) => {
                    const {
                      companyName = '-',
                      first_name = '-',
                      email = '-',
                      taxNumber = '-',
                      startDate = '-',
                      renewalDate = '-',
                      isSuspended = false,
                    } = user || {};

                    return (
                      <tr
                        key={user._id || idx}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3" style={{ width: columns[0].width, minWidth: columns[0].width }}>
                          <div
                            className="font-medium text-gray-900 overflow-auto max-h-16"
                            title={companyName}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f7fafc',
                            }}
                          >
                            {companyName}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ width: columns[1].width, minWidth: columns[1].width }}>
                          <div
                            className="text-gray-900 overflow-auto max-h-16"
                            title={first_name}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f7fafc',
                            }}
                          >
                            {first_name}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ width: columns[2].width, minWidth: columns[2].width }}>
                          <div
                            className="text-gray-900 overflow-auto max-h-16"
                            title={email}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f7fafc',
                            }}
                          >
                            {email}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ width: columns[3].width, minWidth: columns[3].width }}>
                          <div
                            className="text-gray-900 overflow-auto max-h-16"
                            title={taxNumber}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f7fafc',
                            }}
                          >
                            {taxNumber}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ width: columns[4].width, minWidth: columns[4].width }}>
                          <div
                            className="text-gray-900"
                            title={renewalDate}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f7fafc',
                            }}
                          >
                            {renewalDate ? new Date(renewalDate).toLocaleDateString() : '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ width: columns[5].width, minWidth: columns[5].width }}>
                          <select
                            className={`w-full px-2 py-1 rounded text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all ${
                              isSuspended
                                ? 'bg-red-100 text-red-800 focus:ring-red-200'
                                : 'bg-green-100 text-green-800 focus:ring-green-200'
                            }`}
                            value={isSuspended ? 'Suspended' : 'Active'}
                            onChange={() => changeCorpUserStatus(user)}
                          >
                            <option value="Active">Active</option>
                            <option value="Suspended">Suspended</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Popup */}
      {showConfirmPopup && selectedAdmin ? (
        <ConfirmationPopup
          giveStyle="-5rem"
          yesAction={() => suspendUser(selectedAdmin.id, selectedAdmin.isSuspended)}
          cancelAction={() => {
            setShowConfirmPopup(false);
            setSelectedAdmin(null);
          }}
          title={`${selectedAdmin.isSuspended ? 'Unsuspend' : 'Suspend'} Corporate Users`}
          msg={`Are you sure, you want to ${selectedAdmin.isSuspended ? 'Unsuspend' : 'Suspend'} this user?`}
        />
      ) : null}


      {/* Add User Form Modal */}
      {showAddNewAdminForm && (
        <CorporateUserForm
          setShowAddNewUserForm={setShowAddNewAdminForm}
          fetchUserList={fetchCopAdmin}
        />
      )}
    </div>
  );
};

export default UserWorld;